/**
 * Forecasting Service — API client for ComStock forecasting pipeline.
 *
 * All calls go to the PhD API (REACT_APP_PHD_API_URL).
 */

const PHD_API_BASE = process.env.REACT_APP_PHD_API_URL || 'http://localhost:5002';

async function forecastFetch(endpoint, options = {}) {
    const response = await fetch(`${PHD_API_BASE}${endpoint}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
    });
    return response;
}

async function handleResponse(response) {
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || data.message || `HTTP ${response.status}: ${response.statusText}`);
    }
    return data;
}

// ============================================================
// Data Pipeline (ComStock + Sampling)
// ============================================================

export async function getComstockStatus() {
    const response = await forecastFetch('/api/comstock/status');
    return handleResponse(response);
}

export async function startComstockDownload(buildingType) {
    const response = await forecastFetch('/api/comstock/download', {
        method: 'POST',
        body: JSON.stringify({ building_type: buildingType }),
    });
    return handleResponse(response);
}

export async function getComstockStats() {
    const response = await forecastFetch('/api/comstock/stats');
    return handleResponse(response);
}

export async function getSamplingStatus() {
    const response = await forecastFetch('/api/sampling/status');
    return handleResponse(response);
}

export async function getSamplingCoverage() {
    const response = await forecastFetch('/api/sampling/coverage');
    return handleResponse(response);
}

export async function buildSample(config = {}) {
    const response = await forecastFetch('/api/sampling/build', {
        method: 'POST',
        body: JSON.stringify(config),
    });
    return handleResponse(response);
}

// ============================================================
// Model Training
// ============================================================

export async function listModels() {
    const response = await forecastFetch('/api/models');
    return handleResponse(response);
}

export async function getModelVersion(version) {
    const response = await forecastFetch(`/api/models/${version}`);
    return handleResponse(response);
}

export async function startTraining(config) {
    const response = await forecastFetch('/api/models/train', {
        method: 'POST',
        body: JSON.stringify(config),
    });
    return handleResponse(response);
}

export async function getTrainingStatus(jobId) {
    const response = await forecastFetch(`/api/models/train/${jobId}`);
    return handleResponse(response);
}

// ============================================================
// Validation
// ============================================================

export async function listValidationRuns() {
    const response = await forecastFetch('/api/validation');
    return handleResponse(response);
}

export async function startValidationRun(config) {
    const response = await forecastFetch('/api/validation/run', {
        method: 'POST',
        body: JSON.stringify(config),
    });
    return handleResponse(response);
}

export async function startColdStartTest(config) {
    const response = await forecastFetch('/api/validation/cold-start', {
        method: 'POST',
        body: JSON.stringify(config),
    });
    return handleResponse(response);
}

export async function getValidationRun(runId, full = false) {
    const url = full
        ? `/api/validation/${runId}?full=true`
        : `/api/validation/${runId}`;
    const response = await forecastFetch(url);
    return handleResponse(response);
}

// ============================================================
// Forecast (Cold-Start Prediction)
// ============================================================

export async function runPrediction(buildingMetadata, weather8760) {
    const response = await forecastFetch('/api/models/active/predict', {
        method: 'POST',
        body: JSON.stringify({
            building_metadata: buildingMetadata,
            weather_8760: weather8760,
        }),
    });
    return handleResponse(response);
}
